import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Repeat } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Select,
} from "../../shared/ui";
import type {
  ChangeBreakdownRow,
  DateChangeReport,
  PageResponse,
  ProjectSummary,
  ScheduleChangeView,
} from "../../shared/types/api";
import { ApprovalDrawer } from "./ApprovalDrawer";
import {
  CHANGE_TYPE_LABELS,
  REASON_LABELS,
  formatDays,
  formatPercent,
  formatSignedDays,
} from "./changeVocabulary";

const WINDOWS = [
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last quarter" },
  { key: "180", label: "Last six months" },
  { key: "365", label: "Last year" },
];

/**
 * §60's date change report.
 *
 * <p>Ordered the way the question is actually asked. How much did we slip, then what caused it,
 * then who and where — and last, which specific items keep moving, because that is the only part
 * anybody can act on directly.
 *
 * <p>Every breakdown is a share of *days lost* rather than of change count. A reason behind twenty
 * one-day moves is not a bigger problem than one behind a month-long wait, and §60's own example
 * ("Dependency Delay = 32%") is plainly about where the time went.
 */
export function ChangeAnalyticsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [days, setDays] = useState("90");
  const [projectId, setProjectId] = useState("");
  const [deciding, setDeciding] = useState<ScheduleChangeView | null>(null);

  const to = new Date();
  const from = new Date(to.getTime() - Number(days) * 86_400_000);

  const report = useQuery({
    queryKey: ["change-analytics", days, projectId],
    queryFn: () => {
      const params = new URLSearchParams({ from: iso(from), to: iso(to) });
      if (projectId) {
        params.set("projectId", projectId);
      }
      return api.get<DateChangeReport>(`/api/v1/change-analytics/date-changes?${params}`);
    },
  });

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
  });

  const pending = useQuery({
    queryKey: ["schedule-changes", "awaiting-approval"],
    queryFn: () =>
      api.get<ScheduleChangeView[]>("/api/v1/schedule-changes/awaiting-approval"),
    enabled: can("SCHEDULE_CHANGE_APPROVE"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["schedule-changes"] });
    void queryClient.invalidateQueries({ queryKey: ["change-analytics"] });
    void queryClient.invalidateQueries({ queryKey: ["work-item-schedule-changes"] });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Schedule changes"
        subtitle="Where the time went, and what moved it"
        actions={
          <div className="flex items-center gap-2">
            <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Every project</option>
              {projects.data?.content.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectKey} · {project.name}
                </option>
              ))}
            </Select>
            <Select value={days} onChange={(event) => setDays(event.target.value)}>
              {WINDOWS.map((window) => (
                <option key={window.key} value={window.key}>
                  {window.label}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {can("SCHEDULE_CHANGE_APPROVE") && pending.data && pending.data.length > 0 && (
        <Card>
          <CardHeader
            title="Waiting for your sign-off"
            subtitle="Commitments moved outwards on projects with an agreed baseline"
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-ink-100">
              {pending.data.map((change) => (
                <li
                  key={change.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-ink-900">
                      <Link
                        to={`/work-items/${change.workItemKey}`}
                        className="font-mono text-xs text-ink-500 hover:text-brand-600"
                      >
                        {change.workItemKey}
                      </Link>{" "}
                      {change.workItemTitle}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {CHANGE_TYPE_LABELS[change.changeType]} {change.oldDate} →{" "}
                      {change.newDate} · {REASON_LABELS[change.reasonCode]} ·{" "}
                      {change.changedByName}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="tnum text-sm text-amber-700">
                      {formatDays(change.slippageDays)}
                    </span>
                    <Button size="sm" onClick={() => setDeciding(change)}>
                      Decide
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {report.isLoading ? (
        <LoadingState label="Building the report" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : report.data ? (
        <Report data={report.data} />
      ) : null}

      <ApprovalDrawer change={deciding} onClose={() => setDeciding(null)} onSaved={refresh} />
    </div>
  );
}

function Report({ data }: { data: DateChangeReport }) {
  if (data.changeCount === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="No dates moved in this window"
            message="That is the answer, not an empty screen: nothing was rescheduled in the period selected."
          />
        </CardBody>
      </Card>
    );
  }

  const ownSlippage = data.totalSlippageDays - data.externalSlippageDays;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure
          label="Items rescheduled"
          value={String(data.rescheduledItemCount)}
          note={`${data.changeCount} changes in total`}
        />
        <Figure
          label="Days lost, net"
          value={formatSignedDays(data.totalSlippageDays)}
          note={`${data.slipCount} out, ${data.pulledInCount} pulled in`}
          tone={data.totalSlippageDays > 0 ? "amber" : "green"}
        />
        <Figure
          label="Average per change"
          value={
            data.averageSlippageDays == null
              ? "—"
              : `${data.averageSlippageDays.toFixed(1)} days`
          }
        />
        <Figure
          label="From outside the team"
          value={`${data.externalSlippageDays} days`}
          note={`${ownSlippage} days our own`}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Breakdown title="By reason" subtitle="Share of days lost" rows={data.byReason} labelled />
        <Breakdown title="By project" rows={data.byProject} />
        <Breakdown title="By person" rows={data.byEmployee} />
        <Breakdown
          title="By team"
          subtitle="The team they were in on the day, not the team they are in now"
          rows={data.byTeam}
        />
      </div>

      <Card>
        <CardHeader
          title="Moved again and again"
          subtitle="Three or more changes to the same item — a pattern rather than a correction"
        />
        <CardBody className="p-0">
          {data.repeatedlyRescheduled.length === 0 ? (
            <EmptyState
              title="Nothing has moved three times"
              message="Items are being rescheduled once and then holding."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.repeatedlyRescheduled.map((item) => (
                <li
                  key={item.workItemId}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-ink-900">
                      <Link
                        to={`/work-items/${item.workItemKey}`}
                        className="font-mono text-xs text-ink-500 hover:text-brand-600"
                      >
                        {item.workItemKey}
                      </Link>{" "}
                      {item.workItemTitle}
                    </p>
                    <p className="mt-0.5 flex flex-wrap gap-1.5 text-xs text-ink-500">
                      {item.reasons.map((reason) => (
                        <span key={reason} className="rounded bg-ink-100 px-1.5 py-0.5">
                          {REASON_LABELS[reason]}
                        </span>
                      ))}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-ink-600">
                      <Repeat className="h-3.5 w-3.5" />
                      {item.changeCount}
                    </span>
                    <span className="tnum w-24 text-right text-amber-700">
                      {formatDays(item.totalSlippageDays)}
                    </span>
                    <span className="tnum w-24 text-right text-ink-500">
                      {item.currentDueDate ?? "no date"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

/**
 * One of §60's breakdowns.
 *
 * <p>The bar is drawn against the largest row rather than against the total, so a spread of a
 * dozen small reasons stays readable. The percentage beside it is the honest share of the whole.
 */
function Breakdown({
  title,
  subtitle,
  rows,
  labelled,
}: {
  title: string;
  subtitle?: string;
  rows: ChangeBreakdownRow[];
  labelled?: boolean;
}) {
  const peak = rows.reduce((most, row) => Math.max(most, Math.abs(row.slippageDays)), 0);

  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-sm text-ink-500">Nothing to show for this window.</p>
        ) : (
          <ul className="space-y-2.5">
            {rows.map((row) => (
              <li key={row.key}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-ink-800">
                    {labelled
                      ? (REASON_LABELS[row.key as keyof typeof REASON_LABELS] ?? row.label)
                      : row.label}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="tnum text-ink-500">{row.changeCount}×</span>
                    <span
                      className={
                        row.slippageDays > 0
                          ? "tnum w-20 text-right text-amber-700"
                          : row.slippageDays < 0
                            ? "tnum w-20 text-right text-emerald-700"
                            : "tnum w-20 text-right text-ink-500"
                      }
                    >
                      {formatSignedDays(row.slippageDays)} d
                    </span>
                    <span className="tnum w-10 text-right text-ink-500">
                      {formatPercent(row.share)}
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
                  <div
                    className={
                      row.slippageDays >= 0
                        ? "h-full rounded-full bg-amber-500"
                        : "h-full rounded-full bg-emerald-500"
                    }
                    style={{
                      width: peak === 0 ? "0%" : `${(Math.abs(row.slippageDays) / peak) * 100}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Figure({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "amber" | "green";
}) {
  return (
    <div className="rounded-xl border border-ink-200 bg-surface px-4 py-3">
      <p className="text-xs text-ink-500">{label}</p>
      <p
        className={
          "tnum mt-1 text-2xl font-semibold " +
          (tone === "amber"
            ? "text-amber-700"
            : tone === "green"
              ? "text-emerald-700"
              : "text-ink-900")
        }
      >
        {value}
      </p>
      {note && <p className="mt-0.5 text-xs text-ink-500">{note}</p>}
    </div>
  );
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}
